import _debounce from 'lodash/debounce';
import React, { useEffect } from 'react';
import { sharedTableStyles } from '../../../../utils/tableStyles';
import { TableSkeleton } from '../../../../components/UI/Skeleton/Skeleton';
import TableCard from '../../../../components/UI/TableToolbar/TableCard';
import TableToolbar from '../../../../components/UI/TableToolbar/TableToolbar';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';

import { useDeleteKPIMutation } from '../../../../redux/apis/KPIApi';
import { useGetPerformanceQuery } from '../../../../redux/apis/PerformanceApi';
import { handleYearChange, setSelectedPerformance } from '../../../../redux/Slice/performanceSlice';

import EmptyData from '../../../../components/UI/EmptyData';
import DeleteModel from '../../../../components/common/DeleteModel';
import { DepartmentsData } from '../../../../components/Data/AppData';

import PerformanceFormModel from './PerformanceFormModel';
import './PerformanceTable.scss';

import {
  skipRow,
  paginationRow,
  formattedAverage,
  getMonthNumberByName
} from '../../../../utils/handler';
import { LB_HEADER } from '../../../../utils/constant/labels';
import { notification } from '../../../../utils/notification';
import { TOAST_TYPE } from '../../../../utils/constant/errorMessage';
import { GLOBAL, KPI_START_YEAR, MONTHS } from '../../../../utils/constant/global';
import { INPUT_LABEL } from '../../../../utils/constant/placeHolders';

const { TOAST_ERROR } = TOAST_TYPE;
const { DESC, ASC } = GLOBAL;
const { LB_DELETE_KPI, LB_DELETE_KPI_MESSAGE } = INPUT_LABEL;
const { LB_PERFORMANCE_EDIT, LB_PERFORMANCE_ADD } = LB_HEADER;

/* const customStyles = {
  headCells: {
    style: {
      justifyContent: 'center',
      textAlign: 'center'
    }
  },
  cells: {
    style: {
      justifyContent: 'center',
      textAlign: 'center'
    }
  }
}; */

const PerformanceTable = ({ state, setState }) => {
  const dispatch = useDispatch();
  const currentYear = useSelector((state) => state.performance.currentYear);

  const {
    data: performanceData,
    isLoading,
    isFetching,
    refetch: performanceFetch
  } = useGetPerformanceQuery({
    search: state?.search || '',
    skip: skipRow(state?.currentPage, state?.rowsPerPage),
    limit: state?.rowsPerPage,
    sort: state?.sortField,
    sortType: state.sortDirection ? DESC : ASC,
    currentYear
  });

  const [deleteKpi] = useDeleteKPIMutation();

  useEffect(() => {
    dispatch(handleYearChange(new Date().getFullYear()));
  }, []);

  const augustIndex = MONTHS.indexOf('jul');

  // Generate columns dynamically based on the selected year
  const generateColumns = () => {
    const startMonthIndex = currentYear === KPI_START_YEAR ? augustIndex : 0; // Start from August if year is KPI_START_YEAR, otherwise start from January
    const visibleMonths = MONTHS.slice(startMonthIndex);

    const monthColumns = visibleMonths.map((month) => ({
      name: month.charAt(0).toUpperCase() + month.slice(1), // Capitalize first letter
      selector: (row) => row[month],
      sortable: true,
      width: '124px',
      minWidth: '124px',
      maxWidth: '124px',
      grow: 0,
      center: true,
      cell: (row) => {
        const average =
          row.performances?.find(
            (p) => p.month === getMonthNumberByName[month] && p.year === currentYear
          )?.average || 'Rate Now';
        const textAndBorderColor =
          average < 6.5
            ? 'border-red text-white bg-danger'
            : average >= 6.5 && average < 8
              ? 'border-orange text-white bg-warning'
              : average >= 8
                ? 'border-green text-white bg-success'
                : 'text-black border';

        return (
          <div className="text-center">
            <button
              type="button"
              onClick={() =>
                handleSetEditData({ ...row, selectedYear: currentYear, selectedMonth: month })
              }
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              className={`btn btn-white w-15 ${textAndBorderColor}`}
            >
              <div className="d-flex justify-content-between align-items-center">
                {average !== 'Rate Now' ? formattedAverage(average) : average}
              </div>
            </button>
          </div>
        );
      }
    }));

    return [
      {
        name: 'Name',
        selector: (row) => row.userName,
        sortable: true,
        width: '180px',
        minWidth: '180px',
        maxWidth: '180px',
        grow: 0,
        center: true,
        cell: (row) => <div className="text-center">{row.userName}</div>
      },
      ...monthColumns
    ];
  };

  useEffect(() => {
    performanceFetch();
  }, []);

  // useEffect to call performanceFetch whenever currentYear changes
  useEffect(() => {
    performanceFetch();
  }, [currentYear]);

  // eslint-disable-next-line no-unused-vars
  const handleSetEditData = async (row, currentYear, idx) => {
    setState((prevState) => ({
      ...prevState,
      isModal: { model: true, submit: false },
      editKPIData: row,
      modalHeader: {
        title: row?.performances?.some(
          (p) => p.month === getMonthNumberByName[row.selectedMonth] && p.year === row.selectedYear
        )
          ? LB_PERFORMANCE_EDIT
          : LB_PERFORMANCE_ADD,
        isEdit: true
      }
    }));

    if (row?._id) {
      dispatch(setSelectedPerformance(row));
    }
  };

  const debouncedOnSearchData = _debounce((data) => {
    setState({ ...state, search: data });
  }, 300);

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setState({ ...state, rowsPerPage: newRowsPerPage, currentPage: 1 });
  };

  const handleChangePage = (page) => {
    setState({ ...state, currentPage: page });
  };

  const handleDeleteKPI = (id) => {
    try {
      deleteKpi(id).then((res) => {
        if (res?.data?.success) {
          performanceFetch();
          setState({ ...state, isDelete: { id: '', status: false } });
        }
      });
    } catch (error) {
      notification(error.message, TOAST_ERROR);
    }
  };

  const handleChangeSort = (sortField) => {
    setState({ ...state, sortDirection: !state.sortDirection, sortField: sortField.sortField });
  };

  const onHandleSubmit = () => {
    performanceFetch();
  };

  return (
    <div className="row clearfix g-3">
      <div className="col-sm-12">
        <TableCard>
          <TableToolbar
            onSearch={debouncedOnSearchData}
            actions={
              <div
                className="app-table-year-picker btn-group"
                role="group"
                aria-label="Select year"
              >
                <button
                  type="button"
                  onClick={() => {
                    if (currentYear > KPI_START_YEAR) {
                      dispatch(handleYearChange(currentYear - 1));
                    }
                  }}
                  title="Previous year"
                  className="app-table-year-picker__nav"
                  disabled={KPI_START_YEAR >= currentYear ? true : false}
                >
                  <i className="icofont-rounded-left"></i>
                </button>
                <span className="app-table-year-picker__value">{currentYear || ''}</span>
                <button
                  type="button"
                  onClick={() => {
                    dispatch(handleYearChange(currentYear + 1));
                  }}
                  title="Next year"
                  className="app-table-year-picker__nav"
                >
                  <i className="icofont-rounded-right"></i>
                </button>
              </div>
            }
          />
          <div className="card-body">
            <DataTable
              title={DepartmentsData.title}
              columns={generateColumns()}
              data={performanceData?.data || []}
              defaultSortField="title"
              pagination
              paginationServer
              onSort={handleChangeSort}
              sortField={state.sortField}
              sortDirection={state.sortDirection}
              paginationTotalRows={paginationRow(
                // state.isNext,
                performanceData?.isNext,
                state.rowsPerPage,
                state.currentPage,
                performanceData?.data?.length
              )}
              paginationPerPage={state.rowsPerPage}
              paginationRowsPerPageOptions={[5, 10, 20, 30]}
              paginationComponentOptions={{
                noRowsPerPage: false,
                rangeSeparatorText: 'of',
                selectAllRowsItem: state.isNext,
                selectAllRowsItemText: 'All',
                paginationButtonsDisabled: state.isNext
              }}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              selectableRows={false}
              className="table myDataTable performance-table table-hover align-middle mb-0 d-row nowrap dataTable no-footer dtr-inline"
              highlightOnHover={true}
              noDataComponent={<EmptyData />}
              progressPending={isLoading || isFetching ? true : false}
              progressComponent={<TableSkeleton columns={5} />}
              customStyles={sharedTableStyles}
            />
          </div>
        </TableCard>
        <DeleteModel
          isOpen={() => {
            setState({ ...state, isDelete: { id: '', status: false } });
          }}
          open={state?.isDelete?.status}
          title={LB_DELETE_KPI}
          message={LB_DELETE_KPI_MESSAGE}
          isDelete={() => {
            handleDeleteKPI(state?.isDelete.id);
          }}
        />
      </div>
      {state?.isModal?.model && (
        <PerformanceFormModel state={state} setState={setState} onHandleSubmit={onHandleSubmit} />
      )}
    </div>
  );
};

export default PerformanceTable;
